import React, { useState } from 'react';
import {
  Row,
  Col,
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Typography,
  Popconfirm,
  Modal,
  App,
  Result,
} from 'antd';
import { KeyOutlined, FileTextOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../Components/common/PageHeader';
import { LoadingState, ApiErrorState } from '../../Components/common/StateViews';
import StatusTag from '../../Components/common/StatusTag';
import useApiData from '../../Hooks/useApiData';
import cioService from '../../Services/cioService';
import CommercialTermsModal from '../../Components/customer/CommercialTermsModal';
import DeploymentModal from '../../Components/customer/DeploymentModal';
import SupportAccessPanel from '../../Components/customer/SupportAccessPanel';
import OnboardingGuide from '../../Components/customer/OnboardingGuide';
import {
  ClientStatus,
  ClientStatusLabels,
  ClientStatusColors,
  ClientContactTypeLabels,
  BillingCycleLabels,
  BillingHandoffStatusLabels,
  BillingHandoffStatusColors,
  DeploymentStatusLabels,
  DeploymentStatusColors,
  ClientAdminProvisioningStatusLabels,
  ClientAdminProvisioningStatusColors,
  labelOf,
} from '../../Constants/enums';
import { money, formatDate, formatDateTime } from '../../Utils/format';
import { toApiError } from '../../api/axiosClient';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { data, loading, error, reload } = useApiData(() => cioService.getClient(id), [id]);

  const [termsOpen, setTermsOpen] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [rotating, setRotating] = useState(false);

  // A missing record is a terminal state - a full 404 result is clearest here.
  if (error?.status === 404) {
    return (
      <Result
        status="404"
        title="Customer not found"
        extra={<Button onClick={() => navigate('/cio/customers')}>Back to customers</Button>}
      />
    );
  }

  const changeStatus = async (nextStatus, label) => {
    setStatusBusy(true);
    try {
      await cioService.setStatus(id, nextStatus);
      message.success(`Customer ${label}.`);
      reload();
    } catch (err) {
      // The API rejects invalid transitions with a business-rule message - surface it.
      message.error(toApiError(err).message);
    } finally {
      setStatusBusy(false);
    }
  };

  const rotateKey = async () => {
    setRotating(true);
    try {
      const result = await cioService.rotateIntegrationKey(id);
      Modal.success({
        title: 'Integration key generated',
        content: (
          <div>
            <p>Copy this key now - it is shown only once and cannot be retrieved again.</p>
            <Typography.Paragraph copyable={{ text: result?.integrationKey }} code>
              {result?.integrationKey}
            </Typography.Paragraph>
          </div>
        ),
      });
    } catch (err) {
      message.error(toApiError(err).message);
    } finally {
      setRotating(false);
    }
  };

  const headerActions = data ? (
    <Space wrap>
      <Button onClick={() => setTermsOpen(true)}>Set Commercial Terms</Button>
      <Button onClick={() => setDeployOpen(true)}>Configure Deployment</Button>
      <Popconfirm
        title="Generate a new integration key? The current key stops working."
        onConfirm={rotateKey}
        okText="Rotate"
        cancelText="Cancel"
        okButtonProps={{ loading: rotating }}
      >
        <Button icon={<KeyOutlined />} loading={rotating}>Rotate Integration Key</Button>
      </Popconfirm>
      <Button icon={<FileTextOutlined />} onClick={() => navigate(`/cfo/invoices?clientId=${id}`)}>
        Invoices
      </Button>
      {data.status === ClientStatus.InformationSubmitted && (
        <Popconfirm
          title="Move this customer into commercial review?"
          onConfirm={() => changeStatus(ClientStatus.CommercialReview, 'moved to commercial review')}
          okText="Move"
          cancelText="Cancel"
        >
          <Button loading={statusBusy}>Move to Commercial Review</Button>
        </Popconfirm>
      )}
      {data.status === ClientStatus.Ready && (
        <Popconfirm
          title="Activate this customer? Required deployment, commercial, and payment conditions must be complete."
          onConfirm={() => changeStatus(ClientStatus.Active, 'activated')}
          okText="Activate"
          cancelText="Cancel"
        >
          <Button type="primary" loading={statusBusy}>Activate Customer</Button>
        </Popconfirm>
      )}
      {data.status === ClientStatus.Active && (
        <Popconfirm
          title="Suspend this customer?"
          onConfirm={() => changeStatus(ClientStatus.Suspended, 'suspended')}
          okText="Suspend"
          cancelText="Cancel"
        >
          <Button danger loading={statusBusy}>Suspend</Button>
        </Popconfirm>
      )}
      {data.status === ClientStatus.Suspended && (
        <Popconfirm
          title="Reactivate this customer?"
          onConfirm={() => changeStatus(ClientStatus.Active, 'reactivated')}
          okText="Reactivate"
          cancelText="Cancel"
        >
          <Button type="primary" loading={statusBusy}>Reactivate</Button>
        </Popconfirm>
      )}
    </Space>
  ) : null;

  const subscription = data?.subscription;
  const deployment = data?.deployment;
  const organizationProfile = data?.organizationProfile;
  const administratorProvisioning = data?.administratorProvisioning;
  const contacts = organizationProfile?.contacts || [];

  return (
    <div>
      <PageHeader
        title={
          data ? (
            <Space>
              {data.clientName}
              <StatusTag value={data.status} labels={ClientStatusLabels} colors={ClientStatusColors} />
            </Space>
          ) : (
            'Customer'
          )
        }
        subtitle={data ? `Org ID ${data.publicId} · Account ${data.accountNumber} · ${data.adminContactEmail}` : undefined}
        docTitle={data?.clientName || 'Customer'}
        onBack={() => navigate('/cio/customers')}
        extra={headerActions}
      />

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ApiErrorState error={error} onRetry={reload} />
      ) : (
        <>
          <OnboardingGuide status={data.status} />

          <Row gutter={16}>
            {/* Left column: who the customer is (packs tight, no cross-row gaps) */}
            <Col xs={24} lg={12}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Card title="Organization Profile" size="small">
                  {organizationProfile ? (
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Slug">{organizationProfile.slug || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Address">{organizationProfile.physicalAddress || '-'}</Descriptions.Item>
                      <Descriptions.Item label="City / State">
                        {[organizationProfile.city, organizationProfile.state].filter(Boolean).join(', ') || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Country / Zip">
                        {[organizationProfile.country, organizationProfile.zipCode].filter(Boolean).join(' · ') || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Industry">{organizationProfile.industry?.name || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Terms">
                        {organizationProfile.termsAndConditionsAccepted
                          ? `Accepted (${organizationProfile.termsAndConditionsVersion || '-'})`
                          : 'Not accepted'}
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Typography.Text type="secondary">No organization profile yet.</Typography.Text>
                  )}
                </Card>

                <Card title="Contacts" size="small">
                  {contacts.length === 0 ? (
                    <Typography.Text type="secondary">No contacts captured yet.</Typography.Text>
                  ) : (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                      {contacts.map((c) => (
                        <div key={c.id ?? `${c.type}-${c.email}`}>
                          <Tag color="#599c16">{labelOf(ClientContactTypeLabels, c.type)}</Tag>
                          <div style={{ marginTop: 4 }}>
                            <strong>{[c.firstName, c.middleName, c.lastName].filter(Boolean).join(' ')}</strong>
                          </div>
                          <div style={{ color: '#64748b' }}>{c.email}{c.phone ? ` · ${c.phone}` : ''}</div>
                        </div>
                      ))}
                    </Space>
                  )}
                </Card>
              </div>
            </Col>

            {/* Right column: the deal and the infrastructure */}
            <Col xs={24} lg={12}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Card title="Subscription & Commercial Terms" size="small">
                  {subscription ? (
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Plan">{subscription.planName || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Seats">{subscription.seatCount ?? '-'}</Descriptions.Item>
                      <Descriptions.Item label="Billing cycle">
                        {labelOf(BillingCycleLabels, subscription.billingCycle)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Start date">{formatDate(subscription.startDate)}</Descriptions.Item>
                      <Descriptions.Item label="Unit price">
                        {money(subscription.unitPrice, subscription.currency)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Estimated total">
                        {money(subscription.estimatedTotal, subscription.currency)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Billing handoff">
                        <StatusTag
                          value={subscription.billingHandoffStatus}
                          labels={BillingHandoffStatusLabels}
                          colors={BillingHandoffStatusColors}
                        />
                      </Descriptions.Item>
                      <Descriptions.Item label="Payment before activation">
                        {subscription.paymentRequiredBeforeActivation ? 'Required' : 'Not required'}
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Typography.Text type="secondary">No commercial terms set yet.</Typography.Text>
                  )}
                </Card>

                <Card title="Dedicated Deployment" size="small">
                  {deployment ? (
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Status">
                        <StatusTag value={deployment.status} labels={DeploymentStatusLabels} colors={DeploymentStatusColors} />
                      </Descriptions.Item>
                      <Descriptions.Item label="Frontend URL">{deployment.frontendUrl || '-'}</Descriptions.Item>
                      <Descriptions.Item label="API base URL">{deployment.apiBaseUrl || '-'}</Descriptions.Item>
                      <Descriptions.Item label="App database">{deployment.appDatabaseName || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Hiring database">{deployment.hiringDatabaseName || '-'}</Descriptions.Item>
                      <Descriptions.Item label="Instance key">{deployment.instanceKey || '-'}</Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Typography.Text type="secondary">No deployment registered yet.</Typography.Text>
                  )}
                </Card>
              </div>
            </Col>
          </Row>

          {/* Org Admin provisioning (full width) */}
          <Card title="Organization Admin Provisioning" size="small" style={{ marginTop: 16 }}>
            {administratorProvisioning ? (
              <Descriptions column={{ xs: 1, sm: 2, lg: 4 }} size="small">
                <Descriptions.Item label="Status">
                  <StatusTag
                    value={administratorProvisioning.status}
                    labels={ClientAdminProvisioningStatusLabels}
                    colors={ClientAdminProvisioningStatusColors}
                  />
                </Descriptions.Item>
                <Descriptions.Item label="Invited">{formatDateTime(administratorProvisioning.invitationCreatedAtUtc)}</Descriptions.Item>
                <Descriptions.Item label="Activated">{formatDateTime(administratorProvisioning.activatedAtUtc)}</Descriptions.Item>
                <Descriptions.Item label="Last error">
                  {administratorProvisioning.lastError || '-'}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Typography.Text type="secondary">Provisioning has not started.</Typography.Text>
            )}
          </Card>

          {/* Controlled support-access audit (Remaining Handoff, Flow E) */}
          <div style={{ marginTop: 16 }}>
            <SupportAccessPanel clientId={id} />
          </div>

          <CommercialTermsModal
            open={termsOpen}
            onClose={() => setTermsOpen(false)}
            clientId={id}
            initial={subscription}
            onSaved={reload}
          />
          <DeploymentModal
            open={deployOpen}
            onClose={() => setDeployOpen(false)}
            clientId={id}
            initial={deployment}
            onSaved={reload}
          />
        </>
      )}
    </div>
  );
};

export default CustomerDetail;
